import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './hooks/useAuth';
import { useSocket, initSocket, disconnectSocket } from './hooks/useSocket';
import { useApi } from './hooks/useApi';
import { alertsApi, agentsApi, retailersApi, productsApi, getToken } from './api/client';
import type { Alert, AgentState, Retailer, Product } from './api/client';
import { GLOBAL_CSS, T } from './components/ui';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import RegisterPage    from './pages/RegisterPage';
import OnboardingPage  from './pages/OnboardingPage';
import DashboardPage   from './pages/DashboardPage';
import AlertsPage      from './pages/AlertsPage';
import ProductsPage    from './pages/ProductsPage';
import AgentsPage      from './pages/AgentsPage';
import RetailersPage   from './pages/RetailersPage';
import ApiKeysPage     from './pages/ApiKeysPage';
import ControlPage     from './pages/ControlPage';
import ProvenancePage  from './pages/ProvenancePage';
import ProcurementPage     from './pages/ProcurementPage';
import RecallPage          from './pages/RecallPage';
import TransparencyRequestsPage from './pages/TransparencyRequestsPage';
import DppPage             from './pages/DppPage';
import IotPage             from './pages/IotPage';
import SupplierPortalPage  from './pages/SupplierPortalPage';
import BillingPage         from './pages/BillingPage';
import UsersPage           from './pages/UsersPage';
import ImportPage          from './pages/ImportPage';
import WebhooksPage        from './pages/WebhooksPage';
import AnalyticsPage       from './pages/AnalyticsPage';
import ReportsPage         from './pages/ReportsPage';

type View = 'dashboard' | 'alerts' | 'products' | 'provenance' | 'agents' |
            'procurement' | 'retailers' | 'apikeys' | 'control' |
            'recall' | 'dpp' | 'iot' | 'portal' |
            'transparency' |
            'billing' | 'users' | 'import' | 'webhooks' |
            'analytics' | 'reports';

// Simple hash routing for register
function getHash() { return window.location.hash.replace('#', ''); }

export default function App() {
  const { user, loading: authLoading, logout } = useAuth();
  const [view, setView] = useState<View>('dashboard');
  const [hash, setHash] = useState(getHash());

  useEffect(() => {
    const handler = () => setHash(getHash());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  // ── Global live state ─────────────────────────────
  const [alerts,    setAlerts]    = useState<Alert[]>([]);
  const [agents,    setAgents]    = useState<AgentState[]>([]);
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [products,  setProducts]  = useState<Product[]>([]);
  const [heartbeat, setHeartbeat] = useState<{ agentsActive: number; openAlerts: number; uptime: number } | null>(null);

  const { data: alertData,   refetch: refetchAlerts }   = useApi(() => alertsApi.list({ limit: 50 }), [user]);
  const { data: agentData,   refetch: refetchAgents }   = useApi(() => agentsApi.list(), [user]);
  const { data: retailerData }                          = useApi(() => retailersApi.list(), [user]);
  const { data: productData, refetch: refetchProducts } = useApi(() => productsApi.list({ limit: 100 }), [user]);

  useEffect(() => { if (alertData)    setAlerts(alertData.alerts);       }, [alertData]);
  useEffect(() => { if (agentData)    setAgents(agentData);              }, [agentData]);
  useEffect(() => { if (retailerData) setRetailers(retailerData);        }, [retailerData]);
  useEffect(() => { if (productData)  setProducts(productData.products); }, [productData]);

  // ── WebSocket ─────────────────────────────────────
  const handleAlertNew = useCallback((event: { payload: Alert }) => {
    setAlerts(prev => [event.payload, ...prev.filter(a => a.id !== event.payload.id)]);
  }, []);
  const handleAlertUpdated = useCallback((event: { payload: Partial<Alert> & { id: string } }) => {
    setAlerts(prev => prev.map(a => a.id === event.payload.id ? { ...a, ...event.payload } : a));
  }, []);
  const handleAgentStatus = useCallback((event: { payload: AgentState }) => {
    setAgents(prev => prev.map(a => a.id === event.payload.id || a.agent_type === event.payload.agent_type
      ? { ...a, ...event.payload } : a));
  }, []);
  const handleProductScore = useCallback((event: { payload: { id: string; provenance_score: number; risk_level: string } }) => {
    setProducts(prev => prev.map(p => p.id === event.payload.id
      ? { ...p, provenance_score: event.payload.provenance_score, risk_level: event.payload.risk_level as Product['risk_level'] }
      : p));
  }, []);
  const handleHeartbeat = useCallback((event: { payload: { agentsActive: number; openAlerts: number; uptime: number } }) => {
    setHeartbeat(event.payload);
  }, []);

  useSocket({
    'alert:new':             handleAlertNew     as any,
    'alert:updated':         handleAlertUpdated as any,
    'agent:status':          handleAgentStatus  as any,
    'product:score_updated': handleProductScore as any,
    'system:heartbeat':      handleHeartbeat    as any,
  });

  useEffect(() => {
    if (user) {
      const token = getToken();
      if (token) initSocket(token);
    } else {
      disconnectSocket();
    }
  }, [user]);

  // ── Loading ───────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: `3px solid ${T.border}`, borderTopColor: T.copper, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  // ── Not logged in ─────────────────────────────────
  if (!user) {
    if (hash === 'register') {
      return <RegisterPage onLoginClick={() => { window.location.hash = ''; }} />;
    }
    return <LoginPage />;
  }

  // ── Onboarding (new tenants) ──────────────────────
  // Show onboarding if tenant exists but onboarding not complete
  if (user.tenant_id && !(user as any).onboarding_completed) {
    return <OnboardingPage onComplete={() => window.location.reload()} />;
  }

  const pageProps = {
    alerts, agents, retailers, products, heartbeat,
    refetchAlerts, refetchAgents, refetchProducts,
    setAlerts,
  };

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{ display: 'flex', minHeight: '100vh', background: T.bg }}>
        <Sidebar
          active={view}
          onNav={(v) => setView(v as View)}
          alerts={alerts}
          agents={agents}
          user={user}
          onLogout={logout}
        />
        <main style={{ marginLeft: 220, flex: 1, padding: '32px 36px', minHeight: '100vh', overflowX: 'hidden' }}>
          {heartbeat && (
            <div style={{
              position: 'fixed', top: 0, left: 220, right: 0, zIndex: 50,
              background: `${T.bg}e8`, backdropFilter: 'blur(12px)',
              borderBottom: `1px solid ${T.border}`,
              padding: '7px 36px', display: 'flex', alignItems: 'center', gap: 20,
            }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.sage, animation: 'pulse 2s infinite' }} />
                <span style={{ fontSize: 10, color: T.sub }}>Live</span>
              </div>
              <span style={{ fontSize: 10, color: T.sub }}>{heartbeat.agentsActive} agents active</span>
              <span style={{ fontSize: 10, color: T.sub }}>{heartbeat.openAlerts} open alerts</span>
              <span style={{ fontSize: 10, color: T.dim, marginLeft: 'auto' }}>
                Uptime {Math.floor(heartbeat.uptime / 60)}m
              </span>
            </div>
          )}
          <div style={{ marginTop: heartbeat ? 36 : 0 }}>
            {view === 'dashboard'    && <DashboardPage   {...pageProps} />}
            {view === 'alerts'       && <AlertsPage      {...pageProps} />}
            {view === 'products'     && <ProductsPage    {...pageProps} />}
            {view === 'provenance'   && <ProvenancePage  {...pageProps} />}
            {view === 'agents'       && <AgentsPage />}
            {view === 'procurement'  && <ProcurementPage {...pageProps} />}
            {view === 'retailers'    && <RetailersPage   {...pageProps} />}
            {view === 'apikeys'      && <ApiKeysPage />}
            {view === 'control'      && <ControlPage        {...pageProps} />}
            {view === 'recall'       && <RecallPage />}
            {view === 'transparency' && <TransparencyRequestsPage />}
            {view === 'dpp'          && <DppPage />}
            {view === 'iot'          && <IotPage />}
            {view === 'portal'       && <SupplierPortalPage />}
            {view === 'billing'      && <BillingPage />}
            {view === 'users'        && <UsersPage />}
            {view === 'import'       && <ImportPage />}
            {view === 'webhooks'     && <WebhooksPage />}
            {view === 'analytics'    && <AnalyticsPage />}
            {view === 'reports'      && <ReportsPage />}
          </div>
        </main>
      </div>
    </>
  );
}
